/**
 * RecipeTab Component
 *
 * Main recipe editing interface shown as a tab/section in the phase editor.
 * Allows selecting a recipe template, viewing/editing ingredients, and cost summary.
 *
 * US-P2-010: Process Recipe Integration - Task 3
 */

import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import {
  useProcessRecipe,
  useProcessRecipeMutations
} from '@/hooks/useProcessRecipes'
import {
  RECIPE_TYPE_OPTIONS,
  RECIPE_TYPE_LABELS
} from '@/hooks/useRecipeTemplates'
import { useAllStrains } from '@/hooks/useStrains'

import RecipeSelector from './RecipeSelector'
import RecipeLineList from './RecipeLineList'
import RecipeLineEditor from './RecipeLineEditor'
import RecipeCostSummary from './RecipeCostSummary'

import {
  Package,
  Plus,
  Save,
  AlertCircle,
  Trash2,
  X,
  RefreshCw
} from 'lucide-react'

/**
 * Create Recipe Dialog - form for creating a new recipe template
 */
function CreateRecipeDialog({
  open,
  onOpenChange,
  recipeType,
  strainId,
  onCreated
}) {
  const { strains } = useAllStrains({ activeOnly: true })
  const { createRecipe, loading } = useProcessRecipeMutations()

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    recipe_type: recipeType || 'block',
    strain_id: strainId || ''
  })
  const [errors, setErrors] = useState({})

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        name: '',
        description: '',
        recipe_type: recipeType || 'block',
        strain_id: strainId || ''
      })
      setErrors({})
    }
  }, [open, recipeType, strainId])

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.name.trim()) {
      newErrors.name = 'Recipe name is required'
    }
    if (!formData.recipe_type) {
      newErrors.recipe_type = 'Recipe type is required'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleCreate = async () => {
    if (!validate()) return

    try {
      const recipe = await createRecipe({
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        recipe_type: formData.recipe_type,
        strain_id: formData.strain_id || null
      })

      toast.success('Recipe created')
      onCreated(recipe)
      onOpenChange(false)
    } catch (err) {
      toast.error(`Failed to create recipe: ${err.message}`)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Create New Recipe
          </DialogTitle>
          <DialogDescription>
            Create a recipe template that can be reused across processes
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="recipe-name">
              Recipe Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="recipe-name"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder="e.g., Standard Oyster Block Mix"
            />
            {errors.name && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.name}
              </p>
            )}
          </div>

          {/* Recipe Type */}
          <div className="space-y-2">
            <Label htmlFor="recipe-type">
              Recipe Type <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.recipe_type}
              onValueChange={(value) => handleFieldChange('recipe_type', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECIPE_TYPE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.recipe_type && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.recipe_type}
              </p>
            )}
          </div>

          {/* Strain (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="recipe-strain">
              Strain (Optional)
            </Label>
            <Select
              value={formData.strain_id || 'none'}
              onValueChange={(value) => handleFieldChange('strain_id', value === 'none' ? '' : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Generic (all strains)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Generic (all strains)</SelectItem>
                {strains.map(strain => (
                  <SelectItem key={strain.id} value={strain.id}>
                    {strain.name} ({strain.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Leave blank to create a generic recipe usable for any strain
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="recipe-description">Description</Label>
            <Textarea
              id="recipe-description"
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Optional description..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={loading}>
            {loading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Create Recipe
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * RecipeTab - Main recipe editor interface
 *
 * @param {string} phaseId - Process phase ID
 * @param {string} processId - Process ID
 * @param {string} recipeType - Default recipe type for this phase
 * @param {string} strainId - Optional strain ID for strain-specific filtering
 * @param {string} currentRecipeId - Currently linked recipe template ID
 * @param {function} onRecipeChange - Callback when recipe selection changes
 * @param {boolean} readOnly - Whether the editor is read-only
 */
export default function RecipeTab({
  // phaseId and processId reserved for future use
  recipeType = 'block',
  strainId = null,
  currentRecipeId = null,
  onRecipeChange,
  readOnly = false
}) {
  // State
  const [selectedRecipeId, setSelectedRecipeId] = useState(currentRecipeId)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showLineEditor, setShowLineEditor] = useState(false)
  const [editingLine, setEditingLine] = useState(null)
  const [deleteConfirmLine, setDeleteConfirmLine] = useState(null)

  // Hooks
  const { recipe, lines, loading, error, refetch } = useProcessRecipe(selectedRecipeId)
  const {
    addRecipeLine,
    updateRecipeLine,
    removeRecipeLine,
    loading: mutationLoading
  } = useProcessRecipeMutations()

  // Sync with prop changes
  useEffect(() => {
    setSelectedRecipeId(currentRecipeId)
  }, [currentRecipeId])

  // Handle recipe selection change
  const handleRecipeChange = useCallback((newRecipeId) => {
    setSelectedRecipeId(newRecipeId)
    if (onRecipeChange) {
      onRecipeChange(newRecipeId)
    }
  }, [onRecipeChange])

  // Handle new recipe created
  const handleRecipeCreated = useCallback((newRecipe) => {
    handleRecipeChange(newRecipe.id)
  }, [handleRecipeChange])

  // Handle add ingredient
  const handleAddLine = () => {
    setEditingLine(null)
    setShowLineEditor(true)
  }

  // Handle edit ingredient
  const handleEditLine = (line) => {
    setEditingLine(line)
    setShowLineEditor(true)
  }

  // Handle save ingredient
  const handleSaveLine = async (lineData) => {
    if (!selectedRecipeId) return

    try {
      if (editingLine) {
        await updateRecipeLine(editingLine.id, lineData)
        toast.success('Ingredient updated')
      } else {
        await addRecipeLine(selectedRecipeId, lineData)
        toast.success('Ingredient added')
      }
      setShowLineEditor(false)
      setEditingLine(null)
      refetch()
    } catch (err) {
      toast.error(`Failed to save ingredient: ${err.message}`)
    }
  }

  // Handle delete ingredient
  const handleDeleteLine = (line) => {
    setDeleteConfirmLine(line)
  }

  const confirmDeleteLine = async () => {
    if (!deleteConfirmLine) return

    try {
      await removeRecipeLine(deleteConfirmLine.id)
      toast.success('Ingredient removed')
      setDeleteConfirmLine(null)
      refetch()
    } catch (err) {
      toast.error(`Failed to remove ingredient: ${err.message}`)
    }
  }

  return (
    <div className="space-y-4">
      {/* Recipe Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-4 w-4" />
            Recipe Template
            {recipe && (
              <Badge variant="secondary" className="text-xs ml-2">
                {RECIPE_TYPE_LABELS[recipe.recipe_type] || recipe.recipe_type}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Select a recipe template to define the ingredients for this phase
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RecipeSelector
            recipeType={recipeType}
            strainId={strainId}
            value={selectedRecipeId}
            onChange={handleRecipeChange}
            onCreateNew={() => setShowCreateDialog(true)}
            disabled={readOnly}
          />
        </CardContent>
      </Card>

      {/* Recipe Content - show when recipe is selected */}
      {selectedRecipeId && (
        <>
          {loading ? (
            <Card>
              <CardContent className="py-8">
                <div className="flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin mr-3" />
                  <span className="text-muted-foreground">Loading recipe...</span>
                </div>
              </CardContent>
            </Card>
          ) : error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : recipe && (
            <>
              {/* Recipe Details */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{recipe.name}</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={refetch}
                      disabled={loading}
                      title="Refresh"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                  {recipe.description && (
                    <CardDescription>{recipe.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Ingredients List */}
                  <RecipeLineList
                    lines={lines}
                    onEdit={handleEditLine}
                    onDelete={handleDeleteLine}
                    readOnly={readOnly}
                    saving={mutationLoading}
                  />

                  {/* Add Ingredient Button */}
                  {!readOnly && (
                    <Button
                      variant="outline"
                      className="w-full border-dashed"
                      onClick={handleAddLine}
                      disabled={mutationLoading}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Ingredient
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Cost Summary */}
              <RecipeCostSummary
                recipeId={selectedRecipeId}
                quantity={1}
                showQuantityInput={true}
              />
            </>
          )}
        </>
      )}

      {/* Create Recipe Dialog */}
      <CreateRecipeDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        recipeType={recipeType}
        strainId={strainId}
        onCreated={handleRecipeCreated}
      />

      {/* Line Editor Dialog */}
      <RecipeLineEditor
        open={showLineEditor}
        onOpenChange={setShowLineEditor}
        line={editingLine}
        onSave={handleSaveLine}
        saving={mutationLoading}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmLine}
        onOpenChange={(open) => !open && setDeleteConfirmLine(null)}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Remove Ingredient
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove "{deleteConfirmLine?.inventory_item?.name || 'this ingredient'}" from the recipe?
              <br /><br />
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmLine(null)}
              disabled={mutationLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteLine}
              disabled={mutationLoading}
            >
              {mutationLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
